// gaji.view — menu 💰 Penggajian. Merakit angka yang SUDAH dihitung modul lain:
//   komisi  → hitungKomisi (payout.view, sumber yang sama dengan menu Teknisi/Komisi)
//   absensi → rekapKehadiran (@domain/attendance, sumber yang sama dengan Rekap Absensi)
//   part    → kerugianByTekBulan (.tek, basis tanggal RETUR — dasar potong gaji)
// Tak satu pun dihitung ulang di sini; kalau dihitung ulang, cepat atau lambat menu ini dan
// menu asalnya akan menyebut angka berbeda untuk orang yang sama.
import { rp, rpFmt, rpVal, esc, dtLocal } from '@domain/format';
import { currentUser } from '@core/session';
import { bisaBuka } from '@core/perms';
import { tarifPotongan, absensi } from '@core/settings';
import { hitungSlip, potonganKehadiran, jumlahKomponen, type Komponen } from '@domain/gaji';
import { rekapKehadiran, cutiDaysInPeriod } from '@domain/attendance';
import { openModal } from '@ui/modal';
import { toast } from '@ui/toast';
import { fetchAll } from '@modules/reports/reports.repo';
import * as absRepo from '@modules/absensi/absensi.repo';
import * as partRepo from '@modules/sparepart/sparepart.repo';
import { listAkun } from '@modules/payments/akunkas.repo';
import * as payoutRepo from './payout.repo';
import { hitungKomisi } from './payout.view';
import { kerugianByTekBulan, lymd } from './finance.view';
import * as repo from './gaji.repo';
import * as svc from './gaji.service';

const now = new Date();
// Periode LOKAL menu ini — jangan warisi fBulan/fTahun milik finance.view (state bersama
// seluruh layar keuangan; mewarisinya pernah membuat tombol Simpan hilang tanpa penjelasan).
let gBln = now.getMonth() + 1, gThn = now.getFullYear(), gCab = '';

const BLN = ['', 'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];

interface Baris {
  empId: number; nama: string; role: string; outlet: string | null;
  komisi: number; komisiDibayar: number;
  kehadiran: { telat: number; setengah: number; alpha: number; istLebih: number; hadir: number; cuti: number };
  rugiPart: number;
  slip: repo.Slip | null;
  /** Slip periode sebelumnya — ancang-ancang pokok & tunjangan supaya tak diketik ulang tiap bulan. */
  lalu: repo.Slip | null;
}

export async function renderPenggajian(root: HTMLElement): Promise<void> {
  const u = currentUser();
  if (!u || !['Owner', 'Manager', 'Admin Finance'].includes(u.role)) {
    root.innerHTML = '<div class="ttl">💰 Penggajian</div><div class="panel"><div class="empty">Khusus Owner, Manager, dan Admin Finance.</div></div>';
    return;
  }
  root.innerHTML = '<div class="ttl">💰 Penggajian</div><div class="panel"><div class="empty">Memuat…</div></div>';
  const periode = `${gThn}-${String(gBln).padStart(2, '0')}`;

  let items: Awaited<ReturnType<typeof fetchAll>>;
  let skema: payoutRepo.KomisiEmp[];
  let emps: repo.EmpGaji[];
  let payouts: payoutRepo.Payout[];
  let att: absRepo.RekapRow[]; let leaves: absRepo.Leave[];
  let losses: partRepo.TekLoss[] = [];
  let slips: repo.Slip[]; let sebelum: repo.Slip[];
  let akunKas: { id: number; nama: string; jenis: string; aktif: boolean }[] = [];
  try {
    [items, skema, emps, payouts, att, leaves, slips, sebelum] = await Promise.all([
      fetchAll(), payoutRepo.fetchKomisiSchemes(), repo.listKaryawanGaji(), payoutRepo.listByPeriode(periode),
      absRepo.listAttendance(), absRepo.listLeaves(),
      repo.listByPeriode(periode), repo.listSebelum(periode),
    ]);
    try { losses = await partRepo.listRejectedReturns(); } catch { /* part opsional */ }
    try { akunKas = (await listAkun()).filter((a) => a.aktif); } catch { /* akun kas opsional */ }
  } catch (e) {
    root.innerHTML = `<div class="ttl">💰 Penggajian</div><div class="panel err">${esc((e as Error).message)}</div>`;
    return;
  }

  // ── rakit bahan ─────────────────────────────────────────────────────────────
  const komisiOrang = hitungKomisi(items, skema, gBln, gThn);
  const attPer = att.filter((r) => +r.tgl.slice(0, 4) === gThn && +r.tgl.slice(5, 7) === gBln);
  // Pulang cepat tidak dipakai memotong gaji → penilai selalu false (angkanya tak diperlukan di sini).
  const rekap = rekapKehadiran(attPer, absensi().istirahatMenit, () => false);
  // Kerugian part: basis tanggal RETUR (bukan tanggal ambil) — itu yang jadi dasar potong gaji.
  const lossPer = losses.filter((l) => lymd(l.at).slice(0, 7) === periode);
  const aggRugi = kerugianByTekBulan(lossPer, losses);
  const slipOf = new Map(slips.map((s) => [s.emp_id, s]));
  const slipLalu = new Map<number, repo.Slip>();
  for (const s of sebelum) if (!slipLalu.has(s.emp_id)) slipLalu.set(s.emp_id, s); // urut periode desc → yang pertama = terbaru

  const rows: Baris[] = emps
    .filter((e) => !gCab || e.outlet === gCab)
    .map((e) => {
      const kom = komisiOrang.filter((o) => o.nama === e.nama).reduce((a, o) => a + o.komisi, 0);
      // 'pending' & 'approved' ikut mengunci, sama seperti layar Bayar Komisi. Kalau hanya
      // 'paid' yang dihitung, komisi yang sedang diajukan akan ikut netto slip juga → dibayar dua
      // kali begitu batchnya cair.
      const dibayar = payouts.filter((p) => p.emp_nama === e.nama && ['paid', 'pending', 'approved'].includes(p.status)).reduce((a, p) => a + p.amount, 0);
      // Cocokkan lewat id, bukan nama: dua karyawan bernama sama (atau baris absensi yang
      // join-nya gagal sehingga namanya '—') akan membebankan potongan orang lain.
      const k = rekap.get(e.id);
      const cutiHari = cutiDaysInPeriod(
        leaves.filter((l) => l.emp_id === e.id && l.status === 'approved').map((l) => ({ from: l.from_tgl, to: l.to_tgl })),
        gThn, gBln);
      return {
        empId: e.id, nama: e.nama, role: e.role, outlet: e.outlet,
        komisi: kom, komisiDibayar: dibayar,
        kehadiran: { telat: k?.telat ?? 0, setengah: k?.setengah ?? 0, alpha: k?.alpha ?? 0, istLebih: k?.istLebih ?? 0, hadir: k?.hadir ?? 0, cuti: cutiHari },
        rugiPart: e.role === 'Teknisi' ? (aggRugi.get(e.nama)?.tek ?? 0) : 0,   // SPV tak menanggung part
        slip: slipOf.get(e.id) ?? null,
        lalu: slipLalu.get(e.id) ?? null,
      };
    })
    .sort((a, b) => a.nama.localeCompare(b.nama));

  const cabs = [...new Set(emps.map((e) => e.outlet).filter((x): x is string => !!x))].sort();
  const totNetto = rows.reduce((a, r) => a + (r.slip?.netto ?? 0), 0);
  const belum = rows.filter((r) => !r.slip).length;
  const tarif = tarifPotongan();
  const tarifKosong = !tarif.telat && !tarif.setengah && !tarif.alpha && !tarif.istirahatPerMenit;

  const badge = (s: repo.Slip | null): string => {
    if (!s) return '<span class="pill" style="background:var(--ice);color:var(--muted)">belum dibuat</span>';
    if (s.status === 'paid') return `<span class="pill" style="background:var(--okbg);color:var(--ok)">✔ dibayar${s.dibayar_at ? ' ' + esc(dtLocal(s.dibayar_at).slice(0, 10)) : ''}</span>`;
    if (s.status === 'approved') return '<span class="pill" style="background:var(--warnbg);color:var(--warn)">disetujui</span>';
    return '<span class="pill" style="background:var(--ice);color:var(--navy)">draf</span>';
  };

  const body = rows.map((r) => {
    const s = r.slip;
    return `<tr>
      <td><b>${esc(r.nama)}</b> <span style="font-size:11px;color:var(--muted)">${esc(r.role)}${r.outlet ? ' · ' + esc(r.outlet) : ''}</span></td>
      <td class="r">${s ? rp(s.gaji_pokok) : '—'}</td>
      <td class="r">${s ? rp(jumlahKomponen(s.tunjangan)) : '—'}</td>
      <td class="r">${r.komisi ? rp(r.komisi) : '—'}${r.komisiDibayar ? `<div style="font-size:10.5px;color:var(--ok)">✔ ${rp(r.komisiDibayar)} dibayar/diajukan terpisah</div>` : ''}${
        // Netto slip DIBEKUKAN saat simpan, sedangkan komisi dihitung ulang tiap render. Verifikasi
        // Finance yang menyusul membuat keduanya berbeda tanpa tanda apa pun — di sinilah tandanya.
        s && s.status !== 'paid' && s.komisi !== Math.max(0, r.komisi - r.komisiDibayar)
          ? `<div style="font-size:10.5px;color:var(--warn)">⚠ berubah sejak slip dibuat (slip: ${rp(s.komisi)}) — buka & simpan ulang</div>` : ''}</td>
      <td class="r" style="color:${r.kehadiran.alpha || r.kehadiran.telat ? 'var(--warn)' : 'var(--muted)'}">${r.kehadiran.alpha ? `${r.kehadiran.alpha} alpha` : ''}${r.kehadiran.alpha && r.kehadiran.telat ? ' · ' : ''}${r.kehadiran.telat ? `${r.kehadiran.telat} telat` : ''}${!r.kehadiran.alpha && !r.kehadiran.telat ? '—' : ''}</td>
      <td class="r" style="color:${r.rugiPart ? 'var(--danger)' : 'var(--muted)'}">${r.rugiPart ? rp(r.rugiPart) : '—'}</td>
      <td class="r"><b>${s ? rp(s.netto) : '—'}</b></td>
      <td>${badge(s)}</td>
      <td style="white-space:nowrap">
        <button class="btn" style="padding:2px 8px;font-size:11.5px" data-slip="${r.empId}">${s ? '✏️ Ubah' : '➕ Buat'}</button>
        ${s ? `<button class="ico-btn" data-cetak="${s.id}" title="Cetak slip" aria-label="Cetak slip ${esc(r.nama)}">🖨️</button>` : ''}
        ${s && s.status === 'draft' && svc.bolehSetujui() ? `<button class="btn" style="padding:2px 8px;font-size:11.5px" data-setuju="${s.id}">✅ Setujui</button>` : ''}
        ${s && s.status === 'approved' ? `<button class="btn primary" style="padding:2px 8px;font-size:11.5px" data-bayar="${s.id}">💵 Bayar</button>` : ''}
      </td></tr>`;
  }).join('') || '<tr><td colspan="9" class="empty">Belum ada karyawan pada filter ini.</td></tr>';

  root.innerHTML = `<div class="ttl">💰 Penggajian</div>
    <div class="scope-note">Merakit angka yang sudah dihitung aplikasi: komisi (menu Teknisi/Komisi), kehadiran (Rekap Absensi), dan tanggungan kerugian part. <b>Gaji pokok &amp; tunjangan diisi di sini</b> dan bulan berikutnya terisi otomatis dari slip terakhir.</div>
    ${tarifKosong ? '<div class="scope-note" style="background:var(--warnbg);color:var(--warn)">Tarif potongan kehadiran belum disetel — telat/alpha <b>tidak memotong apa pun</b>. Setel di Pengaturan → Penggajian bila memang ingin memotong.</div>' : ''}
    <div class="toolbar" style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;margin:10px 0">
      <select id="g_bln">${BLN.map((b, i) => i ? `<option value="${i}" ${gBln === i ? 'selected' : ''}>${b}</option>` : '').join('')}</select>
      <select id="g_thn">${[gThn + 1, gThn, gThn - 1, gThn - 2].map((y) => `<option ${gThn === y ? 'selected' : ''}>${y}</option>`).join('')}</select>
      ${cabs.length > 1 ? `<select id="g_cab"><option value="">Semua cabang</option>${cabs.map((c) => `<option ${gCab === c ? 'selected' : ''}>${esc(c)}</option>`).join('')}</select>` : ''}
      <span style="margin-left:auto;font-size:12.5px">Total netto <b>${rp(totNetto)}</b>${belum ? ` · <span style="color:var(--warn)">${belum} slip belum dibuat</span>` : ''}</span>
    </div>
    <div style="overflow-x:auto"><table class="data"><thead><tr>
      <th>Karyawan</th><th class="r">Gaji pokok</th><th class="r">Tunjangan</th><th class="r">Komisi</th>
      <th class="r">Kehadiran</th><th class="r">Kerugian part</th><th class="r">Netto</th><th>Status</th><th></th>
    </tr></thead><tbody>${body}</tbody></table></div>`;

  const refresh = (): void => void renderPenggajian(root);
  root.querySelector('#g_bln')?.addEventListener('change', (e) => { gBln = +(e.target as HTMLSelectElement).value; refresh(); });
  root.querySelector('#g_thn')?.addEventListener('change', (e) => { gThn = +(e.target as HTMLSelectElement).value; refresh(); });
  root.querySelector('#g_cab')?.addEventListener('change', (e) => { gCab = (e.target as HTMLSelectElement).value; refresh(); });

  root.querySelectorAll<HTMLElement>('button[data-slip]').forEach((b) => b.addEventListener('click', () => {
    const r = rows.find((x) => x.empId === +(b.dataset.slip ?? -1));
    if (r) dlgSlip(r, periode, tarif, refresh);
  }));
  root.querySelectorAll<HTMLElement>('button[data-cetak]').forEach((b) => b.addEventListener('click', () => {
    const s = slips.find((x) => x.id === +(b.dataset.cetak ?? -1));
    if (s) void cetakSlip(s);
  }));
  root.querySelectorAll<HTMLElement>('button[data-setuju]').forEach((b) => b.addEventListener('click', () => void (async () => {
    const s = slips.find((x) => x.id === +(b.dataset.setuju ?? -1));
    if (!s) return;
    try { await svc.setujui(s); toast('✅ Slip disetujui'); refresh(); } catch (e) { toast((e as Error).message); }
  })()));
  root.querySelectorAll<HTMLElement>('button[data-bayar]').forEach((b) => b.addEventListener('click', () => {
    const s = slips.find((x) => x.id === +(b.dataset.bayar ?? -1));
    if (s) dlgBayar(s, akunKas, refresh);
  }));
}

/** Dialog isi slip. Potongan otomatis DITAMPILKAN dan bisa diubah — bukan dipaksakan diam-diam. */
function dlgSlip(r: Baris, periode: string, tarif: ReturnType<typeof tarifPotongan>, done: () => void): void {
  const s = r.slip;
  // Belum ada slip bulan ini → pakai slip terakhir sebagai ancang-ancang. Gaji pokok jarang
  // berubah; mengetiknya ulang untuk sepuluh orang tiap bulan adalah cara paling mudah salah.
  const pokokAwal = s?.gaji_pokok ?? r.lalu?.gaji_pokok ?? 0;
  const tunjSumber = s?.tunjangan?.length ? s.tunjangan : (r.lalu?.tunjangan ?? []);
  const tunjAwal: Komponen[] = tunjSumber.length ? tunjSumber : [{ nama: 'Tunjangan', jumlah: 0 }];
  // Penyebut "sehari gaji" = setelan, bukan turunan absensi (lihat TarifPotongan.hariKerjaSebulan).
  const hariKerja = Math.max(1, tarif.hariKerjaSebulan || 26);
  const otomatis: Komponen[] = [
    ...potonganKehadiran(r.kehadiran, tarif, pokokAwal, hariKerja),
    ...(r.rugiPart > 0 ? [{ nama: 'Tanggungan kerugian part', jumlah: r.rugiPart }] : []),
  ];
  const potAwal: Komponen[] = s?.potongan?.length ? s.potongan : otomatis;
  // Komisi yang sudah dibayar lewat menu Bayar Komisi TIDAK diulang di slip — kalau tidak,
  // orang yang sama dibayar dua kali untuk komisi yang sama.
  const komisiSlip = Math.max(0, r.komisi - r.komisiDibayar);

  const barisKomp = (list: Komponen[], pre: string): string => list.map((c, i) => `
    <div style="display:flex;gap:6px;margin-bottom:4px">
      <input class="${pre}_n" value="${esc(c.nama)}" placeholder="keterangan" style="flex:1">
      <input class="${pre}_v" value="${rpFmt(String(c.jumlah))}" inputmode="numeric" style="width:130px;text-align:right">
      <button type="button" class="ico-btn" data-del="${pre}:${i}" title="Hapus baris" aria-label="Hapus baris ${esc(c.nama)}">🗑️</button>
    </div>`).join('');

  const bg = openModal(`<div class="modal-head"><h3>💰 Slip Gaji — ${esc(r.nama)} · ${esc(periode)}</h3><button class="x">×</button></div>
    <div class="modal-body">
      ${s?.status === 'paid' ? '<div class="scope-note" style="background:var(--okbg);color:var(--ok)">Slip sudah dibayar — angkanya terkunci.</div>' : ''}
      ${!s && r.lalu ? `<div class="scope-note">Terisi dari slip <b>${esc(r.lalu.periode)}</b>. Ubah bila ada perubahan.</div>` : ''}
      <div class="field"><label>Gaji pokok</label><input id="s_pokok" inputmode="numeric" value="${rpFmt(String(pokokAwal))}"></div>

      <div class="ttl" style="font-size:13px;margin:10px 0 4px">Tunjangan</div>
      <div id="s_tunj">${barisKomp(tunjAwal, 'tj')}</div>
      <button type="button" class="btn" id="s_addtj" style="padding:2px 9px;font-size:11.5px">➕ Baris tunjangan</button>

      <div class="info-row" style="margin-top:10px"><span class="k">Komisi periode ini</span><span><b>${rp(komisiSlip)}</b>${r.komisiDibayar ? `<div style="font-size:11px;color:var(--ok)">${rp(r.komisiDibayar)} sudah dibayar lewat menu Bayar Komisi — tidak diulang di sini</div>` : ''}</span></div>

      <div class="ttl" style="font-size:13px;margin:10px 0 4px">Potongan
        <span style="font-size:11px;font-weight:400;color:var(--muted)">(terisi otomatis dari kehadiran &amp; kerugian part — boleh diubah)</span></div>
      <div id="s_pot">${barisKomp(potAwal, 'pt')}</div>
      <button type="button" class="btn" id="s_addpt" style="padding:2px 9px;font-size:11.5px">➕ Baris potongan</button>

      <div class="info-row" style="margin-top:10px;border-top:2px solid var(--navy);padding-top:6px">
        <span class="k"><b>Netto dibayar</b></span><span id="s_netto"><b>—</b></span></div>
      <div id="s_sisa" class="scope-note" style="display:none;color:var(--danger)"></div>

      <div class="field" style="margin-top:8px"><label>Catatan</label><input id="s_cat" value="${esc(s?.catatan ?? '')}" placeholder="opsional"></div>
      <div class="scope-note" style="font-size:11.5px">Kehadiran periode ini: hadir ${r.kehadiran.hadir} · telat ${r.kehadiran.telat} · ½ hari ${r.kehadiran.setengah} · alpha ${r.kehadiran.alpha}${r.kehadiran.cuti ? ` · cuti ${r.kehadiran.cuti}` : ''}.</div>
      <div class="err"></div></div>
    <div class="modal-foot">
      <button class="btn" id="s_batal">Batal</button>
      ${s && s.status !== 'paid' && svc.bolehSetujui() ? `<button class="btn" id="s_hapus" style="border-color:var(--danger);color:var(--danger)">🗑️ Hapus slip</button>` : ''}
      ${s?.status === 'paid' ? '' : '<button class="btn primary" id="s_simpan">💾 Simpan slip</button>'}
    </div>`);

  const baca = (pre: string): Komponen[] => {
    const n = [...bg.querySelectorAll<HTMLInputElement>('.' + pre + '_n')];
    const v = [...bg.querySelectorAll<HTMLInputElement>('.' + pre + '_v')];
    return n.map((el, i) => ({ nama: el.value.trim() || '—', jumlah: rpVal(v[i]?.value ?? '') }))
      .filter((c) => c.jumlah > 0);
  };
  // Potongan otomatis bergantung pada gaji pokok (tarif alpha "sehari gaji"), sedangkan pokoknya
  // baru diketik SETELAH dialog terbuka. Tanpa hitung ulang, slip baru selalu lahir tanpa
  // potongan alpha — dan tak ada yang tahu kenapa. Hitung ulang berhenti begitu petugas menyentuh
  // blok potongan: angka yang sudah dia ubah tak boleh ditimpa diam-diam.
  let potDisentuh = !!s?.potongan?.length;
  const isiOtomatis = (): void => {
    if (potDisentuh) return;
    const pokok = rpVal((bg.querySelector('#s_pokok') as HTMLInputElement).value);
    const baru2: Komponen[] = [
      ...potonganKehadiran(r.kehadiran, tarif, pokok, hariKerja),
      ...(r.rugiPart > 0 ? [{ nama: 'Tanggungan kerugian part', jumlah: r.rugiPart }] : []),
    ];
    (bg.querySelector('#s_pot') as HTMLElement).innerHTML = barisKomp(baru2, 'pt');
    wire();
  };
  const hitung = (): void => {
    const h = hitungSlip(rpVal((bg.querySelector('#s_pokok') as HTMLInputElement).value), baca('tj'), komisiSlip, baca('pt'));
    (bg.querySelector('#s_netto') as HTMLElement).innerHTML = `<b>${rp(h.netto)}</b> <span style="font-size:11px;color:var(--muted)">(bruto ${rp(h.bruto)} − potongan ${rp(h.totalPotongan)})</span>`;
    const sisa = bg.querySelector('#s_sisa') as HTMLElement;
    sisa.style.display = h.sisaPotongan ? '' : 'none';
    sisa.textContent = h.sisaPotongan ? `Potongan melebihi gaji sebesar ${rp(h.sisaPotongan)} — netto ditahan di Rp 0. Sisanya diputuskan manual, tidak dibawa otomatis ke bulan depan.` : '';
  };
  const wire = (): void => {
    bg.querySelectorAll<HTMLInputElement>('input[inputmode="numeric"],.tj_v,.pt_v').forEach((el) => {
      if (el.dataset['wired']) return; el.dataset['wired'] = '1';
      el.addEventListener('input', () => { el.value = el.value.trim() ? rpFmt(el.value) : ''; hitung(); });
    });
    bg.querySelectorAll<HTMLInputElement>('.tj_n,.pt_n').forEach((el) => {
      if (el.dataset['wired']) return; el.dataset['wired'] = '1';
      el.addEventListener('input', hitung);
    });
    bg.querySelectorAll<HTMLElement>('button[data-del]').forEach((b) => {
      if (b.dataset['wired']) return; b.dataset['wired'] = '1';
      b.addEventListener('click', () => { potDisentuh = true; b.parentElement?.remove(); hitung(); });
    });
    bg.querySelectorAll<HTMLElement>('#s_pot input').forEach((el) => {
      if (el.dataset['tsentuh']) return; el.dataset['tsentuh'] = '1';
      el.addEventListener('input', () => { potDisentuh = true; });
    });
    const pk = bg.querySelector('#s_pokok') as HTMLInputElement | null;
    if (pk && !pk.dataset['auto']) { pk.dataset['auto'] = '1'; pk.addEventListener('input', () => { isiOtomatis(); hitung(); }); }
  };
  const tambah = (host: string, pre: string): void => {
    const d = document.createElement('div');
    d.style.cssText = 'display:flex;gap:6px;margin-bottom:4px';
    d.innerHTML = `<input class="${pre}_n" placeholder="keterangan" style="flex:1">
      <input class="${pre}_v" inputmode="numeric" style="width:130px;text-align:right">
      <button type="button" class="ico-btn" data-del="x" title="Hapus baris" aria-label="Hapus baris">🗑️</button>`;
    (bg.querySelector(host) as HTMLElement).appendChild(d);
    wire();
  };
  wire(); isiOtomatis(); hitung();
  bg.querySelector('#s_addtj')?.addEventListener('click', () => tambah('#s_tunj', 'tj'));
  bg.querySelector('#s_addpt')?.addEventListener('click', () => tambah('#s_pot', 'pt'));
  bg.querySelector('#s_batal')?.addEventListener('click', () => bg.remove());
  bg.querySelector('#s_hapus')?.addEventListener('click', () => void (async () => {
    if (!s) return;
    try { await svc.hapus(s); toast('🗑️ Slip dihapus'); bg.remove(); done(); } catch (e) { toast((e as Error).message); }
  })());
  bg.querySelector('#s_simpan')?.addEventListener('click', function (this: HTMLButtonElement) {
    void (async (): Promise<void> => {
      this.disabled = true;
      const pokok = rpVal((bg.querySelector('#s_pokok') as HTMLInputElement).value);
      const tunj = baca('tj'), pot = baca('pt');
      const h = hitungSlip(pokok, tunj, komisiSlip, pot);
      try {
        await svc.simpanSlip({
          periode, emp_id: r.empId, emp_nama: r.nama, emp_role: r.role, outlet: r.outlet,
          gaji_pokok: pokok, tunjangan: tunj, komisi: komisiSlip, potongan: pot, netto: h.netto,
          catatan: (bg.querySelector('#s_cat') as HTMLInputElement).value.trim() || null,
        });
        toast('💾 Slip tersimpan'); bg.remove(); done();
      } catch (e) { (bg.querySelector('.err') as HTMLElement).textContent = (e as Error).message; this.disabled = false; }
    })();
  });
}

function dlgBayar(s: repo.Slip, akun: { id: number; nama: string; jenis: string }[], done: () => void): void {
  const bg = openModal(`<div class="modal-head"><h3>💵 Bayar Gaji — ${esc(s.emp_nama)}</h3><button class="x">×</button></div>
    <div class="modal-body">
      <div class="info-row"><span class="k">Periode</span><span>${esc(s.periode)}</span></div>
      <div class="info-row"><span class="k">Netto dibayar</span><span><b>${rp(s.netto)}</b></span></div>
      <div class="field"><label>Dibayar via <span class="req">*</span></label><select id="b_via"><option>Transfer</option><option>Tunai</option></select></div>
      ${akun.length ? `<div class="field"><label>Keluar dari akun <span class="req">*</span></label><select id="b_akun"><option value="">— pilih akun —</option>${akun.map((a) => `<option value="${a.id}">${esc(a.nama)}</option>`).join('')}</select>
        <div class="hint">Wajib: tanpa akun sumber, beban gaji tercatat sebagai <b>hutang</b> dan saldo kas tidak berkurang — padahal uangnya sudah keluar.</div></div>`
        : '<div class="scope-note" style="color:var(--warn)">Belum ada akun kas. Siapkan di Pengaturan → 💵 Akun Kas supaya pembayaran gaji mengurangi saldo kas; tanpa itu bebannya tercatat sebagai hutang.</div>'}
      <div class="scope-note" style="font-size:11px">Tercatat sebagai beban kategori <b>Gaji</b> hari ini.</div>
      <div class="err"></div></div>
    <div class="modal-foot"><button class="btn" id="b_no">Batal</button><button class="btn primary" id="b_ok">💵 Bayar &amp; catat beban</button></div>`);
  bg.querySelector('#b_no')?.addEventListener('click', () => bg.remove());
  bg.querySelector('#b_ok')?.addEventListener('click', function (this: HTMLButtonElement) {
    void (async (): Promise<void> => {
      const akunId = +((bg.querySelector('#b_akun') as HTMLSelectElement | null)?.value || 0) || null;
      if (akun.length && !akunId) { (bg.querySelector('.err') as HTMLElement).textContent = 'Pilih akun sumber pembayaran.'; return; }
      this.disabled = true;
      try {
        await svc.bayar(s, (bg.querySelector('#b_via') as HTMLSelectElement).value, akunId);
        toast('💵 Gaji dibayar & tercatat di Beban'); bg.remove(); done();
      } catch (e) { (bg.querySelector('.err') as HTMLElement).textContent = (e as Error).message; this.disabled = false; }
    })();
  });
}

/** Slip cetak — dokumen operasional, pola sama dengan slip komisi (tanpa export-guard). */
async function cetakSlip(s: repo.Slip): Promise<void> {
  if (!bisaBuka('penggajian.cetak')) { toast('Anda tak berwenang mencetak slip gaji.'); return; }
  const { printHTML } = await import('@ui/pdf');
  const baris = (l: Komponen[]): string => l.map((c) => `<tr><td style="padding-left:14px">${esc(c.nama)}</td><td class="r">${rp(c.jumlah)}</td></tr>`).join('');
  const bruto = s.gaji_pokok + jumlahKomponen(s.tunjangan) + s.komisi;
  // Tanpa baris ini karyawan menerima kertas yang aritmetikanya tak nyambung: bruto 500rb,
  // potongan 900rb, netto 0 — dan tak ada penjelasan ke mana 400rb sisanya.
  const tertahan = Math.max(0, jumlahKomponen(s.potongan) - bruto);
  printHTML(`slip-gaji-${s.emp_nama}-${s.periode}.pdf`,
    `<h2>SLIP GAJI</h2>
     <p><b>${esc(s.emp_nama)}</b>${s.emp_role ? ` · ${esc(s.emp_role)}` : ''}${s.outlet ? ` · ${esc(s.outlet)}` : ''} · Periode <b>${esc(s.periode)}</b> · No. <b>${esc(s.no ?? '-')}</b></p>
     <table style="width:70%">
       <tr><th>Keterangan</th><th class="r">Jumlah</th></tr>
       <tr><td>Gaji pokok</td><td class="r">${rp(s.gaji_pokok)}</td></tr>
       ${baris(s.tunjangan ?? [])}
       ${s.komisi ? `<tr><td>Komisi</td><td class="r">${rp(s.komisi)}</td></tr>` : ''}
       <tr><td><b>Bruto</b></td><td class="r"><b>${rp(bruto)}</b></td></tr>
       ${(s.potongan ?? []).length ? `<tr><td colspan="2"><b>Potongan</b></td></tr>${baris(s.potongan ?? [])}
       <tr><td><b>Total potongan</b></td><td class="r"><b>(${rp(jumlahKomponen(s.potongan))})</b></td></tr>` : ''}
       ${tertahan ? `<tr><td>Potongan tertahan (tidak dipotong bulan ini)</td><td class="r">${rp(tertahan)}</td></tr>` : ''}
       <tr><td><b>NETTO DITERIMA</b></td><td class="r"><b>${rp(s.netto)}</b></td></tr>
     </table>
     ${tertahan ? `<p><i>Potongan melebihi gaji bulan ini sebesar ${rp(tertahan)}; sisanya tidak dipotong dan tidak dibawa otomatis ke bulan berikutnya.</i></p>` : ''}
     ${s.catatan ? `<p>Catatan: ${esc(s.catatan)}</p>` : ''}
     ${s.status === 'paid' ? `<p>Dibayar ${esc(dtLocal(s.dibayar_at))}${s.dibayar_via ? ` via ${esc(s.dibayar_via)}` : ''} oleh ${esc(s.dibayar_by ?? '')}${s.disetujui_by ? ` · disetujui ${esc(s.disetujui_by)}` : ''}</p>` : '<p><i>Belum dibayar.</i></p>'}
     <br><table style="width:60%"><tr><th>Diterima oleh</th><th>Dibayar oleh</th></tr><tr><td style="height:60px"></td><td></td></tr></table>`);
}
